import { Component, OnInit, Input, ViewChild, forwardRef, Output, EventEmitter, OnChanges, SimpleChange } from '@angular/core';
import { Submission } from '../../model/paper';
import { ReviewsService } from '../../service/reviews.service';
import { Review } from '../../model/paper';
import { AssignReview } from '../../model/assign.review';
import { InfoService } from '../../service/info.service';
import { User } from '../../model/user';
import { FormGroup, FormBuilder, Validators, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UserSearchComponent } from '../user-search/user-search.component';
import { TranslateService } from '@ngx-translate/core';
import { ReviewsConfigurationService } from '../../service/reviews.configuration.service';
import { EventReviewConfiguration } from '../../model/eventReviewConfiguration';
import { NotificationService } from 'src/app/service/notification.service';
import { AuthService } from 'src/app/service/auth.service';
import { UserEventLimit } from 'src/app/component/user-search/user-search.component';
import { forkJoin } from 'rxjs';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TimezoneService } from 'src/app/service/timezone.service';
import { ReviewerFinderComponent } from '../reviewer-finder/reviewer-finder.component';


@Component({
  selector: 'app-assign-reviewers',
  templateUrl: './assign-reviewers.component.html',
  styleUrls: ['./assign-reviewers.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AssignReviewersComponent),
    multi: true
  }]
})
export class AssignReviewersComponent implements OnInit, OnChanges {
  @Input() submission: Submission;
  @Input() assignExternal = false;
  @Input() eventTimeZone: string;

  public showCreateUserForm = false;
  @Input() userIsChair: boolean = false;

  reviews: Array<Review>;
  newReview: AssignReview;

  form: FormGroup;

  reviewConfiguration: EventReviewConfiguration;

  @ViewChild('search') search: UserSearchComponent;
  @ViewChild('searchExternal') searchExternal: UserSearchComponent;

  @Output() reviewsChanged = new EventEmitter<Review[]>();

  canAssignToExternalReviewers: boolean;
  private isAuthorizedForExternal: boolean;
  @Input() assignedReview: Review;
  userEventLimit: UserEventLimit;



  constructor(
    public fb: FormBuilder,
    public reviewsService: ReviewsService,
    public infoService: InfoService,
    public reviewConfigurationService: ReviewsConfigurationService,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private dialog: MatDialog,
    private tzService: TimezoneService
  ) { }

  addReview(user?: User): void {
    // Usa o usuário entregue pelo valueChanges; fallback pro form.
    user = user || this.form.value.user?.[0];
    if (!user) { return; }

    if (this.userHasAssignmentConflict(user)) {
      this.notificationService.notifyError('reviews.assign-conflict-detected', {
        params: { user: `#${user.id} ${user.name}` }
      });
      this.clearSearchInput();   // reseta a seleção mesmo em conflito
    } else {
      const review = new AssignReview();

      review.assignedBy = this.infoService.user.id;

      review.user = user;
      review.dueAt = this.form.value.dueAt;
      review.submission = this.submission.id;

      this.newReview = review;

      this.checkValidReview(() => {
        this.saveReview();
      });
    }
  }


  // Botão NOVO "Encontrar revisores" → diálogo unificado (comitê + bid +
  // interesse). Ao atribuir lá, devolve as revisões criadas e a lista atualiza.
  openFinderDialog(): void {
    this.dialog.open(ReviewerFinderComponent, {
      data: { submissionId: this.submission.id },
      width: '1040px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'find-reviewers-dialog'
    }).afterClosed().subscribe((created: Review[]) => {
      if (created && created.length) {
        this.reviewsService.getReviewsBySubmissionId(this.submission.id).subscribe(r => {
          this.reviews = r;
          this.reviewsChanged.emit(this.reviews);
        });
      }
    });
  }

  clearSearchInput(): void {
    this.form.get('user').setValue([]);
    this.form.get('member').setValue(null);
    this.search?.clearInput();   // limpa o texto da busca (stateCtrl + input)
    this.form.get('dueAt').setValue(this.reviewConfiguration.conferenceDateDue.toDate());
  }

  checkValidReview(callback: () => void) {
    const dueAtPassed = this.newReview.dueAt < this.today();

    if (dueAtPassed) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'reviews.invalid-date-set',
          content: ''
        }
      }).afterClosed().subscribe(confirmed => {
        if (confirmed) {
          callback();
        }
      });
    } else {
      callback();
    }
  }

  saveReview(): void {
    this.newReview.user = this.newReview.user instanceof User ? this.newReview.user.id : this.newReview.user;
    this.reviewsService.createReview(this.newReview).subscribe(review => {
      const user = <User>review.user;
      this.notificationService.notify('messages.assigned-user-review', { params: { user: `#${user.id} ${user.name}` } });
      this.reviews.push(review);
      this.reviewsChanged.emit(this.reviews);
      this.clearSearchInput();   // card gerado → limpa a caixa de busca
    }, err => {
      this.notificationService.notifyError('errors.could-not-create-review');
    });
  }

  ngOnInit() {

    // ESSENCIAIS: só estes dois liberam a renderização da linha
    // (`*ngIf="reviewConfiguration && reviews"`). São os mais leves — não
    // esperam a lista de revisores do evento inteiro.
    forkJoin([
      this.reviewConfigurationService.getReviewsConfiguration(this.submission.event.id),
      this.reviewsService.getReviewsBySubmissionId(this.submission.id)
    ]).subscribe(([rConfiguration, reviews]) => {
      this.reviewConfiguration = rConfiguration;
      this.form = this.fb.group({
        user: ['', [Validators.required]],
        dueAt: [this.reviewConfiguration.conferenceDateDue?.toDate(), [Validators.required]],
        timeZone: [this.tzService.browserTimeZone],
        externalUser: [''],
        member: []
      });

      // Assim que 1 revisor é escolhido (qualquer fonte que seta `user`), cria a
      // revisão de verdade (status "assigned") na hora — sem passo de confirmação.
      // A "confirmação" real é o chair notificar (assigned → invited). Ver
      // [[project_review_assigned_invited_flow]]. addReview() limpa `user` no fim
      // (→ length 0 aqui), evitando re-disparo.
      this.form.get('user').valueChanges.subscribe(val => {
        if (Array.isArray(val) && val.length === 1) {
          this.addReview(val[0]);
        }
      });

      this.reviews = reviews;
      this.updateExternalFlag();   // caso a permissão já tenha chegado
    });

    // SECUNDÁRIO (não bloqueia a linha): permissão de atribuir a revisor externo.
    // (A lista de membros do comitê saiu — hoje é o diálogo "Encontrar revisores"
    // que a busca por conta própria.)
    this.authService.userIsAdminOrChairOrCommittee(this.submission.event.id).subscribe(isAuthorized => {
      this.isAuthorizedForExternal = isAuthorized;
      this.updateExternalFlag();
    });
  }

  // Depende de reviewConfiguration (essencial) + permissão (secundária), que
  // chegam em ordem indeterminada → recalcula quando qualquer um dos dois chega.
  private updateExternalFlag(): void {
    if (this.reviewConfiguration && this.isAuthorizedForExternal !== undefined) {
      this.canAssignToExternalReviewers = this.reviewConfiguration.tpcAssignToExternal && this.isAuthorizedForExternal;
    }
  }

  ngOnChanges(changed: { submission: SimpleChange }): void {
    if (changed.submission && !changed.submission.firstChange) {
      const submission = (<Submission>changed.submission.currentValue);
      const reviews = submission.reviews;

      if (reviews.length > 0) {
        if (reviews[0] instanceof Review) {
          this.reviews = <Review[]>reviews;
        } else {
          this.reviewsService.getReviewsBySubmissionId(submission.id).subscribe(r => {
            this.reviews = r;
          });
        }
      } else {
        this.reviews = [];
      }
    }
  }

  today(): Date {
    return new Date();
  }

  assignToSelectedUser() {
    const user = <User> this.form.value.externalUser[0];

    if (this.userHasAssignmentConflict(user)) {
      this.notificationService.notifyError('reviews.assign-conflict-detected', {
        params: { user: `#${user.id} ${user.name}` }
      });
    } else {
      this.assignExternalReview(user);
    }
  }

  assignExternalReview(user: User) {
    this.reviewsService.assignReview(this.assignedReview.id, user.id).subscribe(review => {
      this.notificationService.notify('messages.assigned-external-user-review', { params: { user: user.firstName + ' ' + user.lastName } });
      this.assignedReview = review;

      this.reviews = [this.assignedReview];
      this.reviewsChanged.emit(this.reviews);
    });
  }

  userHasAssignmentConflict(user: User): boolean {
    return this.submission.isAuthor(user.id) || this.reviewsService.userIsReviewer(user, this.reviews);
  }
}
